import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import { orgSchema, breadcrumb } from "@/lib/seo";
import api from "@/lib/api";
import { MagnifyingGlass, MapPin, ArrowRight } from "@phosphor-icons/react";

const CANTONS = { ZH: "Zürich", BE: "Bern", LU: "Luzern", UR: "Uri", SZ: "Schwyz", OW: "Obwalden", NW: "Nidwalden", GL: "Glarus", ZG: "Zug", FR: "Fribourg", SO: "Solothurn", BS: "Basel-Stadt", BL: "Basel-Landschaft", SH: "Schaffhausen", AR: "Appenzell A.Rh.", AI: "Appenzell I.Rh.", SG: "St. Gallen", GR: "Graubünden", AG: "Aargau", TG: "Thurgau", TI: "Tessin", VD: "Waadt", VS: "Wallis", NE: "Neuenburg", GE: "Genf", JU: "Jura", FL: "Liechtenstein" };

export default function Destinations() {
  const { t, lang } = useI18n();
  const [cities, setCities] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)); }, []);

  const filtered = cities.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  const groups = {};
  filtered.forEach((c) => { (groups[c.canton] = groups[c.canton] || []).push(c); });
  const cantonKeys = Object.keys(groups).sort((a, b) => (CANTONS[a] || a).localeCompare(CANTONS[b] || b));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="destinations-page">
      <Seo
        title={t("seo_destinations_title")}
        description={t("destinations_sub")}
        path="/destinations"
        jsonLd={[orgSchema(), breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_destinations"), path: "/destinations" }])]}
      />
      <p className="font-body text-sm text-[#EF5A1E] font-semibold mb-3">{t("nav_destinations")}</p>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-[1.05] max-w-3xl">{t("destinations_title")}</h1>
      <p className="font-body text-[#5A607A] mt-5 text-lg max-w-2xl">{t("destinations_sub")}</p>
      <p className="font-body text-sm text-[#0B1533] font-semibold mt-4">{cities.length}+ {t("dest_count")}</p>

      <div className="relative max-w-md mt-8">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA0B0]" />
        <input data-testid="dest-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("dest_search")}
          className="w-full bg-white border border-[#E9E5DE] rounded-2xl pl-11 pr-3 py-3.5 font-body text-sm focus:border-[#EF5A1E] focus:ring-2 focus:ring-[#EF5A1E]/15 focus:outline-none" />
      </div>

      <div className="mt-12 space-y-10">
        {cantonKeys.map((ck) => (
          <div key={ck}>
            <h2 className="font-heading font-semibold text-xl tracking-tight text-[#0B1533] mb-4 flex items-center gap-2">
              <span className="w-6 h-6 rounded-md bg-[#EEF1FF] text-[#EF5A1E] text-[10px] font-body font-bold flex items-center justify-center">{ck}</span>
              {CANTONS[ck] || ck}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {groups[ck].map((c) => (
                <Link key={c.id} to={`/destinations/${c.id}`} data-testid={`dest-${c.id}`}
                  className="group flex items-center justify-between gap-3 rounded-2xl border border-[#E9E5DE] bg-white px-5 py-4 hover:border-[#EF5A1E] transition-colors duration-200">
                  <span className="flex items-center gap-2.5 font-body text-sm text-[#0B1533]"><MapPin size={16} className="text-[#EF5A1E]" /> {c.name}</span>
                  <ArrowRight size={16} className="text-[#9CA0B0] group-hover:text-[#EF5A1E] group-hover:translate-x-1 transition-all duration-200" />
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
